import { useState, useEffect, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

export const NotificationBell = () => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch (e) { console.error("Failed to load notifications:", e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.submission_id) navigate(`/submissions/${n.submission_id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full p-2 hover:bg-secondary transition-colors">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 cc-scroll" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b p-3">
          <span className="font-heading font-semibold">Notifications</span>
          <Button variant="ghost" size="sm" onClick={markAll} data-testid="mark-all-read-btn" className="h-7 text-xs">
            <Check className="mr-1 h-3 w-3" /> Mark all read
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">No notifications yet</p>
          )}
          {data.notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`block w-full border-b px-3 py-3 text-left transition-colors hover:bg-secondary ${!n.read ? "bg-secondary/60" : ""}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />}
                <div className={n.read ? "pl-4" : ""}>
                  <p className="text-sm font-semibold">{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
